import { useEffect, useRef } from "react";
import { ensureCompositeFontsReady } from "../lib/compositeFonts";
import {
  COMPOSITE_HEIGHT,
  COMPOSITE_WIDTH,
  paintCompositeFrame,
  applyCanvasDrawQuality,
} from "../lib/compositeRender";
import { COMPOSITE_RECORDER_BITRATES, createMediaRecorder, createMixedCompositeRecorder } from "../lib/videoCapture";
import { unlockAudioContext, registerGameSampleMixHook } from "../lib/playAudio";
import {
  connectGameSampleToMix,
  startCompositeAudioMix,
  stopCompositeAudioMix,
} from "../lib/compositeAudioMix";

export { COMPOSITE_WIDTH, COMPOSITE_HEIGHT } from "../lib/compositeRender";

export const COMPOSITE_FPS = 30;
const FRAME_MS = 1000 / COMPOSITE_FPS;

/**
 * Vidéo admin : webcam plein écran + plateau peint sur canvas (banque, trappes, question, chrono).
 * @param {React.MutableRefObject<number>} [compositeRevRef]
 */
export const useCompositeRecorder = (
  streamRef,
  playScreenRef,
  {
    onBlob,
    onUnavailable,
    enabled = true,
    flashWagerRef,
    compositeRevRef: _compositeRevRef,
    liveOverlayRef,
    recordingBitrates = COMPOSITE_RECORDER_BITRATES,
  } = {},
) => {
  const recorderRef = useRef(null);
  const rafRef = useRef(0);
  const chunksRef = useRef([]);
  const mimeRef = useRef("video/webm");
  const webcamVideoRef = useRef(null);
  const unavailableNotifiedRef = useRef(false);
  const finalizedBlobRef = useRef(null);
  const stopRequestedRef = useRef(false);

  const buildRecordingBlob = () =>
    chunksRef.current.length ? new Blob(chunksRef.current, { type: mimeRef.current }) : null;

  const teardownStreams = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    if (webcamVideoRef.current) {
      webcamVideoRef.current.srcObject = null;
      webcamVideoRef.current = null;
    }
    stopCompositeAudioMix();
    registerGameSampleMixHook(null);
  };

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let retryTimer = null;
    let lastFrameAt = 0;

    const notifyUnavailable = (reason) => {
      if (unavailableNotifiedRef.current || cancelled) return;
      unavailableNotifiedRef.current = true;
      console.warn("[composite]", reason);
      if (onUnavailable) onUnavailable();
    };

    const setupRecorder = (recorder) => {
      mimeRef.current = recorder.mimeType || "video/webm";
      chunksRef.current = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onerror = (ev) => {
        console.warn("[composite] MediaRecorder error", ev);
      };
      recorder.onstop = () => {
        if (onBlob && chunksRef.current.length) {
          onBlob(new Blob(chunksRef.current, { type: mimeRef.current }));
        }
      };
      recorder.start(1000);
      recorderRef.current = recorder;
      return true;
    };

    const tryStartRecorder = (stream, { mixed = false } = {}) => {
      try {
        const { recorder } = mixed
          ? createMixedCompositeRecorder(stream, recordingBitrates)
          : createMediaRecorder(stream, recordingBitrates, { forComposite: true });
        return setupRecorder(recorder);
      } catch (err) {
        console.warn("[composite] MediaRecorder indisponible pour ce flux", err);
        return false;
      }
    };

    const beginMediaRecorder = async (canvas, camStream, mixedDestStream) => {
      const videoTrack = canvas.captureStream(COMPOSITE_FPS).getVideoTracks()[0];
      const mixedTrack = mixedDestStream?.getAudioTracks?.()[0];

      if (mixedTrack && videoTrack) {
        const mixedStream = new MediaStream([videoTrack, mixedTrack]);
        if (tryStartRecorder(mixedStream, { mixed: true })) return true;
        if (tryStartRecorder(mixedStream, { mixed: false })) return true;
      }

      const canvasStream = canvas.captureStream(COMPOSITE_FPS);
      if (mixedTrack) {
        try {
          canvasStream.addTrack(mixedTrack);
        } catch {
          /* ignore */
        }
      } else {
        const directMic = camStream?.getAudioTracks?.()[0];
        if (directMic) {
          try {
            canvasStream.addTrack(directMic);
          } catch {
            /* ignore */
          }
        }
      }
      if (tryStartRecorder(canvasStream)) return true;

      if (tryStartRecorder(canvas.captureStream(COMPOSITE_FPS))) return true;

      teardownStreams();
      return false;
    };

    const startRecording = async (camStream, canvas, ctx, screenEl) => {
      await unlockAudioContext();
      registerGameSampleMixHook(connectGameSampleToMix);
      const mixedDestStream = await startCompositeAudioMix(camStream, screenEl);

      const webcamVideo = document.createElement("video");
      webcamVideo.muted = true;
      webcamVideo.playsInline = true;
      webcamVideo.srcObject = camStream;
      webcamVideoRef.current = webcamVideo;

      await webcamVideo.play().catch(() => {});
      if (cancelled) return;

      await ensureCompositeFontsReady();
      if (cancelled) return;

      // Toujours RAF : si on ne peint que via requestVideoFrameCallback, un stall
      // webcam arrête le canvas et MediaRecorder n'émet plus de frames vidéo.
      const paintOnRaf = (now) => {
        if (cancelled) return;
        try {
          const t = now ?? performance.now();
          if (t - lastFrameAt >= FRAME_MS) {
            lastFrameAt = t;
            paintCompositeFrame(ctx, {
              uiBitmap: null,
              webcamVideo,
              flashWagerRef,
              liveOverlayRef,
            });
          }
        } catch (err) {
          console.warn("[composite] paint frame failed", err);
        }
        rafRef.current = requestAnimationFrame(paintOnRaf);
      };

      rafRef.current = requestAnimationFrame(paintOnRaf);

      if (!(await beginMediaRecorder(canvas, camStream, mixedDestStream))) {
        notifyUnavailable("MediaRecorder indisponible");
      }
    };

    const tryStart = (attempt = 0) => {
      if (cancelled) return;

      const camStream = streamRef?.current;
      const screenEl = playScreenRef?.current;

      if (!camStream || !screenEl) {
        if (attempt < 50) {
          retryTimer = setTimeout(() => tryStart(attempt + 1), 100);
          return;
        }
        notifyUnavailable("caméra ou écran de jeu non prêts");
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width = COMPOSITE_WIDTH;
      canvas.height = COMPOSITE_HEIGHT;
      const ctx = canvas.getContext("2d", { alpha: false });
      applyCanvasDrawQuality(ctx);
      if (!ctx) {
        notifyUnavailable("canvas 2d indisponible");
        return;
      }

      void startRecording(camStream, canvas, ctx, screenEl);
    };

    tryStart();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      cancelAnimationFrame(rafRef.current);

      const rec = recorderRef.current;
      if (rec && rec.state !== "inactive" && !stopRequestedRef.current) {
        rec.onstop = () => {
          const blob = buildRecordingBlob();
          if (blob?.size) finalizedBlobRef.current = blob;
          teardownStreams();
        };
        try {
          if (rec.state === "recording") rec.requestData();
          rec.stop();
        } catch {
          const blob = buildRecordingBlob();
          if (blob?.size) finalizedBlobRef.current = blob;
          teardownStreams();
        }
      } else if (!stopRequestedRef.current) {
        teardownStreams();
      }

      recorderRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const stopRecording = () =>
    new Promise((resolve) => {
      if (finalizedBlobRef.current?.size) {
        resolve(finalizedBlobRef.current);
        return;
      }

      const rec = recorderRef.current;
      if (!rec || rec.state === "inactive") {
        const blob = buildRecordingBlob();
        if (blob?.size) finalizedBlobRef.current = blob;
        teardownStreams();
        resolve(blob?.size ? blob : null);
        return;
      }

      stopRequestedRef.current = true;
      let settled = false;
      const settle = (blob) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        recorderRef.current = null;
        stopRequestedRef.current = false;
        teardownStreams();
        if (blob?.size) finalizedBlobRef.current = blob;
        resolve(blob?.size ? blob : null);
      };

      const timeoutId = setTimeout(() => {
        settle(buildRecordingBlob());
      }, 10_000);

      rec.onstop = () => {
        settle(buildRecordingBlob());
      };

      try {
        if (rec.state === "recording") rec.requestData();
        rec.stop();
      } catch {
        settle(buildRecordingBlob());
      }
    });

  return { stopRecording };
};
